import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Clock, XCircle, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

const STATUS = {
  not_submitted: ["Not Submitted", "bg-slate-100 text-slate-600", Clock],
  pending_review: ["Pending Review", "bg-amber-50 text-amber-700", Clock],
  verified: ["Verified", "bg-teal-50 text-teal-700", ShieldCheck],
  rejected: ["Rejected", "bg-rose-50 text-rose-700", XCircle],
  reverification_required: ["Re-verification Required", "bg-amber-50 text-amber-700", AlertTriangle],
};

export default function KYC() {
  const { refresh } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState("not_submitted");
  const [f, setF] = useState({ full_name: "", dob: "", pan: "", address: "", bank_account: "", ifsc: "", bank_name: "", branch: "", upi: "", aadhaar: "", aadhaar_not_linked: false, aadhaar_doc: "", aadhaar_doc_name: "", pan_doc: "", pan_doc_name: "" });
  const maxDob = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().slice(0, 10);
  const [ifscInfo, setIfscInfo] = useState(null);
  const [ifscLoading, setIfscLoading] = useState(false);
  const [aadOnFile, setAadOnFile] = useState("");

  const lookupIfsc = async (code) => {
    const ifsc = (code || "").trim().toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) { setIfscInfo(null); return; }
    setIfscLoading(true);
    try {
      const r = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (!r.ok) throw new Error("not found");
      const d = await r.json();
      setIfscInfo({ bank: d.BANK, branch: d.BRANCH });
      setF((p) => ({ ...p, bank_name: d.BANK || "", branch: d.BRANCH || "" }));
    } catch { setIfscInfo({ error: true }); setF((p) => ({ ...p, bank_name: "", branch: "" })); }
    finally { setIfscLoading(false); }
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, pan_doc: r.result, pan_doc_name: file.name }));
    r.readAsDataURL(file);
  };

  const onAadhaarFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, aadhaar_doc: r.result, aadhaar_doc_name: file.name }));
    r.readAsDataURL(file);
  };

  useEffect(() => {
    api.get("/kyc").then((r) => {
      setStatus(r.data.status);
      if (r.data.record) { setAadOnFile(r.data.record.aadhaar || ""); setF((p) => ({ ...p, ...r.data.record, aadhaar: "" })); }
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/kyc", f);
      setStatus(data.status);
      await refresh();
      toast.success(t("kyc_submitted"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const [label, cls, Icon] = STATUS[status] || STATUS.not_submitted;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">KYC Verification</h1>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${cls}`} data-testid="kyc-status"><Icon className="w-4 h-4" /> {label}</span>
      </div>

      {status === "verified" && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4">
          Editing any verified detail will reset your status to <b>Re-verification Required</b> and temporarily disable withdrawals until re-approved.
        </p>
      )}

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="kyc-form">
        {[["full_name", "Full Name (as per PAN)", "text"], ["dob", "Date of Birth", "date"], ["pan", "PAN Number", "text"], ["address", "Address", "text"]].map(([k, l, inputType]) => (
          <div key={k}>
            <Label className="font-semibold text-slate-600">{l}</Label>
            <Input type={inputType} required value={f[k]} max={k === "dob" ? maxDob : undefined} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`kyc-${k}`} className="mt-1.5 h-11" />
            {k === "dob" && <p className="text-xs text-slate-400 mt-1" data-testid="kyc-dob-hint">{t("kyc_dob_hint")}</p>}
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="font-semibold text-slate-600">Bank Account</Label><Input value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} data-testid="kyc-bank" className="mt-1.5 h-11" /></div>
          <div>
            <Label className="font-semibold text-slate-600">IFSC</Label>
            <Input value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} onBlur={(e) => lookupIfsc(e.target.value)} data-testid="kyc-ifsc" className="mt-1.5 h-11 uppercase" placeholder="e.g. HDFC0001234" />
            {ifscLoading && <p className="text-xs text-slate-400 mt-1">{t("kyc_ifsc_loading")}</p>}
            {ifscInfo && !ifscInfo.error && <p className="text-xs text-teal-600 mt-1" data-testid="kyc-ifsc-found">✓ {ifscInfo.bank} — {ifscInfo.branch}</p>}
            {ifscInfo && ifscInfo.error && <p className="text-xs text-rose-600 mt-1" data-testid="kyc-ifsc-error">{t("kyc_ifsc_error")}</p>}
          </div>
        </div>
        <div><Label className="font-semibold text-slate-600">UPI ID</Label><Input value={f.upi} onChange={(e) => setF({ ...f, upi: e.target.value })} data-testid="kyc-upi" className="mt-1.5 h-11" placeholder="name@upi" /></div>
        <div>
          <Label className="font-semibold text-slate-600">Aadhaar Number (12-digit, optional)</Label>
          <Input value={f.aadhaar} maxLength={12} onChange={(e) => setF({ ...f, aadhaar: e.target.value.replace(/\D/g, "").slice(0, 12) })} data-testid="kyc-aadhaar" className="mt-1.5 h-11 font-mono-num" placeholder="XXXX XXXX XXXX" />
          <p className="text-xs text-slate-400 mt-1">{t("kyc_aadhaar_note")}{aadOnFile ? t("kyc_aadhaar_onfile", { aad: aadOnFile }) : ""}</p>
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-600" data-testid="kyc-aadhaar-not-linked-wrap">
          <input type="checkbox" checked={f.aadhaar_not_linked} onChange={(e) => setF({ ...f, aadhaar_not_linked: e.target.checked })} data-testid="kyc-aadhaar-not-linked" className="mt-1" />
          {t("kyc_aadhaar_notlinked")}
        </label>
        {f.aadhaar_not_linked && (
          <div>
            <Label className="font-semibold text-slate-600">Aadhaar card document upload (photo/PDF) *</Label>
            <input type="file" accept="image/*,application/pdf" onChange={onAadhaarFile} data-testid="kyc-aadhaar-doc" className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[#0B223D] file:text-white file:font-semibold" />
            {f.aadhaar_doc && <p className="text-xs text-teal-600 mt-1" data-testid="kyc-aadhaar-doc-attached">✓ Attached: {f.aadhaar_doc_name}</p>}
            <p className="text-xs text-slate-400 mt-1">{t("kyc_aadhaar_docnote")}</p>
          </div>
        )}
        <div>
          <Label className="font-semibold text-slate-600">PAN document upload (name must match your full name) *</Label>
          <input type="file" accept="image/*,application/pdf" onChange={onFile} data-testid="kyc-pan-doc" className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[#0B223D] file:text-white file:font-semibold" />
          {f.pan_doc && <p className="text-xs text-teal-600 mt-1" data-testid="kyc-doc-attached">✓ Attached: {f.pan_doc_name}</p>}
        </div>
        <p className="text-xs text-slate-400">Submitting documents does not auto-verify. Our team reviews every KYC manually and checks that the name on your document matches.</p>
        <Button type="submit" data-testid="kyc-submit-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl">Submit for verification</Button>
      </form>
    </div>
  );
}
